import { Text } from "../../../types";
import { CableForSettings } from "../../types/cables";
import { Item } from "../../types/items";
import { AdminRoofSubstructure, RoofSubstructureSize } from "./roofSubstructures";

export type PatrolItem = {
  id: string;
  name: string;
  image?: string;
  order: number;
};

export type RoofCategoryForSettings = Omit<PatrolItem, "image"> & {
  isFlat: boolean;
  minPitch?: number;
  maxPitch?: number;
  defaultPitch?: number;
};

export type RoofTypeForSettings = PatrolItem & {
  hasHips: boolean;
  canBeCustom: boolean;
  canBeMultiple: boolean;
  roofCategory: RoofCategoryForSettings;
};

export type ExcludedItemsForSettings = {
  item: Item;
  roofSubstructure: RoofSubstructureForSettings;
};

export type PatrolNormativeVersionForSettings = PatrolItem & {
  description: string;
  type: "anchor-point" | "life-line";
};

export type PatrolNormativeForSettings = PatrolItem & {
  description: string;
  excludedItems?: ExcludedItemsForSettings[];
  versions: PatrolNormativeVersionForSettings[];
};
export type SystemTypeForSettings = PatrolItem;

export type SystemAbsorberForSettings = {
  systemId: string;
  minSpan: number;
  maxSpanCurve: number;
  maxSpanIntermediate: number;
  maxUsers?: number;
};

export type AbsorberForSettings = {
  item: Item;
  materialId: string;
  order: number;
  systems: SystemAbsorberForSettings[];
};

export type TerminalForSettings = {
  item: Item;
  systemIds: string[];
  forLoop: boolean;
  forNotLoop: boolean;
};

export type MaterialForSettings = PatrolItem & {
  absorbers?: AbsorberForSettings[];
  terminals?: TerminalForSettings[];
};

export type RoofMaterialForSettings = PatrolItem & {
  sheetMaterialRequired: boolean;
};

export type RoofSubstructureForSettings = PatrolItem & {
  seamHeightRequired: boolean;
};

export type SupportForSettings = {
  // id: string;
  system: PatrolSystemForSettings;
  supportMaterial?: MaterialForSettings;
  item: Item;
  maxSeamsSpan?: number;
  hipsSpans?: number[];
  maxInsulationThickness?: number;
  flatRoofCompatible: boolean;
  forTerminalsAndCurves: boolean;
  forIntermediates: boolean;
};

export const fakeSupportExtension: SupportExtensionForSettings = {
  id: "fakeId",
  code: "",
  description: "",
  height: 0,
};

export type SupportExtensionForSettings = Item & {
  height: number;
};

export type SupportExtraForSettings = {
  id: string;
  item: Item;
  roofSubstructure?: AdminRoofSubstructure;
  seamHeight?: number;
  minThickness?: number;
  maxThickness?: number;
  forLifeLines: boolean;
  forAnchorPoints: boolean;
  factor: number;
};

export type SupportOptionalForSettings = {
  id: string;
  item: Item;
  supportExtraDependency?: SupportExtraForSettings;
  forLifeLines: boolean;
  forAnchorPoints: boolean;
  factor: number;
};

export type PatrolSystemClampingForSettings = {
  id: string;
  roofSubstructureId: string;
  includeSelectedComponent: boolean;
  topItem?: Item;
  topExtraItem?: Item;
  bottomItems?: Item[];
};

export type PatrolSystemForSettings = PatrolItem & {
  insulationThicknessRequired: boolean;
  waveSpanRequired: boolean;
  supportMaterialRequired: boolean;
  supportRequired: boolean;
  stepHeightRequired: boolean;
  isLifeLine: boolean;
  isAnchorPoint: boolean;
  normatives?: PatrolNormativeForSettings[];
  supports?: SupportForSettings[];
  supportExtensions?: SupportExtensionForSettings[];
  supportExtras?: SupportExtraForSettings[];
  supportOptionals?: SupportOptionalForSettings[];
  isClampable: boolean;
  clampings?: PatrolSystemClampingForSettings[];
  isSubstructureIndependent?: boolean;
  maxCornersPerLifeline?: number;
};

export type PatrolSystemMappingForSettings = {
  id: string;
  systemId: string;
  roofCategoryId: string;
  roofMaterialId: string;
  roofSubstructureId?: string;
  minSubstructureHeight?: number;
  minSubstructureSize?: RoofSubstructureSize;
  minMetalPlateThickness?: number;
  roofWeightRequired: boolean;
  seamsSpanRequired: boolean;
};

export type PatrolAnchorMappingForSettings = {
  id: string;
  systemId: string;
  roofSubstructureId?: string;
  materialId?: string;
  membraneId?: string;
  item: Item;
  extraItem?: Item;
  minSubstructureSize?: RoofSubstructureSize;
  minSubstructureHeight?: number;
  stepHeight?: number;
  minStepHeight?: number;
  maxStepHeight?: number;
  seamHeight?: number;
  maxSeamsSpan?: number;
  maxInsulationThickness?: number;
  hipsSpans?: number[];
};

export type PatrolSheetMaterialForSettings = {
  id: string;
  material: string;
  minValue: number;
};

export type CurveIntermediateForSettings = {
  id: string;
  item: Item;
  extraItem?: Item;
  minAngle: number;
  maxAngle: number;
  systemTypesIds: string[];
  materialsIds: string[];
  systemsIds: string[];
};

export type CurveForSettings = {
  id: string;
  item: Item;
  extraItem?: Item;
  rightAngle: boolean;
  notRightAngle: boolean;
};

export type ShuttleForSettings = {
  id: string;
  item: Item;
  systemTypeId: string;
  fixed: boolean;
  systemIds: string[];
  supportMaterialIds: string[];
};

export type PatrolSettings = {
  roofTypes: RoofTypeForSettings[];
  roofMaterials: RoofMaterialForSettings[];
  roofSubstructures: RoofSubstructureForSettings[];
  systems: PatrolSystemForSettings[];
  systemMappings: PatrolSystemMappingForSettings[];
  anchorMappings: PatrolAnchorMappingForSettings[];
  sheetMaterials: PatrolSheetMaterialForSettings[];
  seamsSpans?: number[];
  systemTypes?: SystemTypeForSettings[];
  materials: MaterialForSettings[];
  curveIntermediates?: CurveIntermediateForSettings[];
  shuttles?: ShuttleForSettings[];
  cables?: CableForSettings[];
  texts: {
    pitch: Text;
    sheetMaterials: Text;
    substructureHeight: Text;
    substructureSize: Text;
    metalPlateThickness: Text;
    useOnlyAnchorPoints: Text;
    roofWeight: Text;
    seamHeight: Text;
    hipsSpan: Text;
    seamsSpan: Text;
    insulationThickness: Text;
    waveSpan: Text;
    stepHeight: Text;
    clampThickness: Text;
    clamped: Text;
    splitLifeLine: Text;
  };
};

export type PatrolRoofWeight = {
  value: number;
};
export const patrolRoofWeights: PatrolRoofWeight[] = [{ value: 80 }, { value: 200 }];

export type PatrolWaveSpan = {
  value: number;
};
export const patrolWaveSpans: PatrolWaveSpan[] = [{ value: 76 }, { value: 152 }, { value: 304 }];

export type PatrolSeamHeight = {
  value: number;
};
// const seamHeights: PatrolSeamHeight[] = [{ value: 25 }, { value: 65 }];

export type PatrolStepHeight = {
  value: number;
};

// this hipsSpan are temporary, the data have to come from the server/settings
export type PatrolHipsSpan = {
  value: number;
};
export const patrolHipsSpans: PatrolHipsSpan[] = [
  { value: 250 },
  { value: 333 },
  { value: 115 },
  { value: 150 },
  { value: 180 },
  { value: 225 },
  { value: 300 },
  { value: 370 },
  { value: 420 },
];
